import {
  unwrapSingleServiceOrDirect,
  useApiBaseUrl,
  type FinalResponse,
  type UserDetailsResponse,
} from "~~/shared/models";

type AuthStatus = "unknown" | "authed" | "guest";

export const useAuth = () => {
  const status = useState<AuthStatus>("auth:status", () => "unknown");
  const user = useState<UserDetailsResponse | null>("auth:user", () => null);
  const roles = computed<string[]>(() => user.value?.role ?? []);

  const refreshing = useState<Promise<boolean> | null>(
    "auth:refreshing",
    () => null,
  );

  const lastActivityAt = useState<number>("auth:lastActivityAt", () =>
    Date.now(),
  );
  const isIdle = useState<boolean>("auth:isIdle", () => false);

  const refreshTimer = useState<number | null>("auth:refreshTimer", () => null);
  const idleTimer = useState<number | null>("auth:idleTimer", () => null);

  const IDLE_TIMEOUT_MS = 30 * 60_000;
  const REFRESH_EVERY_MS = 10 * 60_000;

  const cookieHeader = () =>
    import.meta.server ? useRequestHeaders(["cookie"]).cookie : undefined;

  const getCsrfCookie = (): string | undefined => {
    // SSR: parse from the forwarded cookie header
    if (import.meta.server) {
      const raw = cookieHeader();
      if (!raw) return undefined;
      const match = raw.match(/(?:^|; )csrf=([^;]*)/);
      return match ? decodeURIComponent(match[1]!) : undefined;
    }
    // Client: read from browser cookies
    if (typeof document === "undefined") return undefined;
    const match = document.cookie.match(/(?:^|; )csrf=([^;]*)/);
    return match ? decodeURIComponent(match[1]!) : undefined;
  };

  const baseHeaders = (method: string = "GET") => {
    const h: Record<string, string> = {
      "Content-Type": "application/json; charset=utf-8",
      "X-Client-Platform-Hint": "web",
      ...(import.meta.server && cookieHeader()
        ? { cookie: cookieHeader()! }
        : {}),
    };
    const m = method.toUpperCase();
    if (m !== "GET" && m !== "HEAD" && m !== "OPTIONS") {
      const csrf = getCsrfCookie();
      if (csrf) h["X-CSRF-Token"] = csrf;
    }
    return h;
  };

  const setGuest = () => {
    user.value = null;
    status.value = "guest";
  };

  const setAuthed = (u: UserDetailsResponse) => {
    user.value = u;
    status.value = "authed";
  };

  const fetchMe = async () => {
    try {
      const baseurl = useApiBaseUrl();
      const responseMe = await $fetch<
        FinalResponse<UserDetailsResponse> | UserDetailsResponse
      >("/authentification/sessions/me", {
        baseURL: baseurl,
        method: "GET",
        credentials: "include",
        headers: baseHeaders(),
      });
      console.log("fetchMe response", responseMe);
      const unwrapped = unwrapSingleServiceOrDirect(responseMe);
      if (!unwrapped.value) {
        setGuest();
        return false;
      }

      setAuthed(unwrapped.value);
      return true;
    } catch (errror) {
      console.log("fetchMe error", errror);
      setGuest();
      return false;
    }
  };

  const refresh = async () => {
    try {
      const baseurl = useApiBaseUrl();
      await $fetch("/authentification/sessions/refresh", {
        baseURL: baseurl,
        method: "POST",
        credentials: "include",
        headers: baseHeaders("POST"),
      });
      return true;
    } catch (errror) {
      console.log("fetchMe error", errror);
      return false;
    }
  };

  const ensureSession = async (opts?: { force?: boolean }) => {
    // Only skip if not forced AND you truly want to avoid calls.
    // For auto-refresh you should force=true.
    if (!opts?.force && status.value === "authed" && user.value) return true;

    if (await fetchMe()) return true;

    if (refreshing.value) return await refreshing.value;

    refreshing.value = (async () => {
      const ok = await refresh();
      if (!ok) return false;
      return await fetchMe();
    })();

    const ok = await refreshing.value;
    refreshing.value = null;
    return ok;
  };

  const logout = async () => {
    try {
      const baseurl = useApiBaseUrl();
      await $fetch("/authentification/users/logout", {
        baseURL: baseurl,
        method: "POST",
        credentials: "include",
        headers: baseHeaders("POST"),
        body: {},
      });
    } catch (errror) {
      console.log("fetchMe error", errror);
      // ignore
    } finally {
      stopAutoRefresh();
      setGuest();
    }
  };

  const markActivity = () => {
    lastActivityAt.value = Date.now();
    isIdle.value = false;
    scheduleIdleCheck();
  };

  const scheduleIdleCheck = () => {
    if (!import.meta.client) return;
    if (idleTimer.value) window.clearTimeout(idleTimer.value);

    idleTimer.value = window.setTimeout(() => {
      const idleFor = Date.now() - lastActivityAt.value;
      isIdle.value = idleFor >= IDLE_TIMEOUT_MS;
      if (isIdle.value) stopAutoRefresh();
    }, IDLE_TIMEOUT_MS + 250);
  };

  const startAutoRefresh = () => {
    if (!import.meta.client) return;
    stopAutoRefresh();

    refreshTimer.value = window.setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      if (isIdle.value) return;
      if (status.value !== "authed") return;

      // force so it doesn't short-circuit
      await ensureSession({ force: true });
    }, REFRESH_EVERY_MS) as unknown as number;
  };

  const stopAutoRefresh = () => {
    if (!import.meta.client) return;
    if (refreshTimer.value) window.clearInterval(refreshTimer.value);
    refreshTimer.value = null;
  };

  return {
    status: computed(() => status.value),
    user: computed(() => user.value),
    roles,
    isIdle: computed(() => isIdle.value),
    markActivity,
    ensureSession,
    startAutoRefresh,
    stopAutoRefresh,
    logout,
  };
};
